import { DeleteFilled } from "@ant-design/icons";
import { Alert, Button, Form, Space } from "antd";
import CustomerSelector from "components/CustomerSelector";
import SilentUserSelector from "components/SilentUserSelector";
import { Roles } from "context/auth";
import { ApplicationUser, Owners, SilentUser } from "models/applicationUsers";
import { useState } from "react";

interface AccountSelectorProps {
    allowMultiple?: boolean;
    value?: Owners;
    onChange?: (owners: Owners) => void;
    onError?: (message: string) => void;
    includeFrozenUsers?: boolean;
}

interface OwnershipSelectorProps extends AccountSelectorProps {
    supportSilentAccounts?: boolean;
}

const SilentAccountSelector = ({ value, allowMultiple, onChange, onError, includeFrozenUsers }: AccountSelectorProps) => {
    const [breaker, setBreaker] = useState<ApplicationUser | undefined>(value?.customers[0]);
    const [silentUsers, setSilentUsers] = useState<(SilentUser | undefined)[]>(value?.silentUsers ?? []);
    const [isSaving, setIsSaving] = useState(false);

    const isValid = () => silentUsers.length > 0 && silentUsers.filter(u => !u).length === 0;

    const silentUserChanged = (index: number, value?: SilentUser) => {
        silentUsers.splice(index, 1, value)
        setSilentUsers(cur => [...cur]);
        notify();
    };

    const notify = () => {
        onChange && onChange({ customers: [breaker], silentUsers });
    }

    return <Space direction="vertical" style={{ width: '100%' }}>
        <Form.Item
            required
            label="Breaker Name"
            help={<Alert message="Limited to customers in specific role(s)" type="info" showIcon />}>
            <CustomerSelector
                includeFrozen={includeFrozenUsers}
                value={value?.customers[0]}
                roles={[Roles.Breaker]}
                onSelected={(value) => {
                    setBreaker(value);
                    setSilentUsers([]);
                    onChange && onChange({ customers: [value], silentUsers: [] });
                }}
                onClear={() => {
                    setBreaker(undefined);
                    setSilentUsers([]);
                    onChange && onChange({ customers: [], silentUsers: [] });
                }}
                disabled={isSaving}
            />

        </Form.Item>
        <Form.Item
            required
            style={{ marginBottom: '0px' }}
            label={allowMultiple ? 'Customer Name(s)' : 'Customer Name'}>
            <SilentUserSelector
                value={silentUsers[0]}
                allowUserCreation
                ownerId={breaker?.id ?? ''}
                disabled={!breaker || isSaving}
                placeholder={breaker === undefined ? "Select a breaker first" : undefined}
                onSelected={user => silentUserChanged(0, user)}
                onClear={() => silentUserChanged(0)}
                onCreating={() => setIsSaving(true)}
                onCreated={(user) => {
                    setIsSaving(false);
                    silentUserChanged(0, user);
                }}
                onError={onError}
            />
        </Form.Item>

        {silentUsers.slice(1).map((user, idx) => {
            const trueIndex = idx + 1;
            return <div key={trueIndex} style={{ display: 'flex' }}>
                <SilentUserSelector
                    value={user}
                    allowUserCreation
                    ownerId={breaker!.id}
                    onSelected={selected => silentUserChanged(trueIndex, selected)}
                    onClear={() => silentUserChanged(trueIndex)}
                    onCreating={() => setIsSaving(true)}
                    onCreated={(user) => {
                        setIsSaving(false);
                        silentUserChanged(trueIndex, user);
                    }}
                    onError={onError}
                    style={{ flexGrow: 1 }}
                    disabled={isSaving}
                />
                <Button danger
                    disabled={isSaving}
                    style={{ marginLeft: '10px', alignSelf: 'center' }}
                    icon={<DeleteFilled />}
                    onClick={() => {
                        silentUsers.splice(trueIndex, 1);
                        setSilentUsers(cur => [...cur]);
                        notify();
                    }} />
            </div>
        })}

        {allowMultiple && <Button
            onClick={() => silentUserChanged(silentUsers.length, undefined)}
            disabled={!breaker || !isValid() || isSaving}>
            Add Another Account
        </Button>}
    </Space>;
}

const ConsignmentAccountSelector = ({ value, allowMultiple, onChange, includeFrozenUsers }: AccountSelectorProps) => {
    const [customers, setCustomers] = useState<(ApplicationUser | undefined)[]>(value?.customers ?? []);

    const isValid = () => customers.length > 0 && customers.filter(u => !u).length === 0;

    const customerChanged = (index: number, value?: ApplicationUser) => {
        customers.splice(index, 1, value)
        setCustomers(cur => [...cur]);
        notify();
    };

    const notify = () => {
        onChange && onChange({ customers, silentUsers: [] });
    }

    return <Space direction="vertical" style={{ width: '100%' }}>
        <Form.Item
            required
            style={{ marginBottom: '0px' }}
            label={allowMultiple ? 'Customer Name(s)' : 'Customer Name'}>
            <CustomerSelector
                includeFrozen={includeFrozenUsers}
                value={value?.customers[0]}
                onSelected={(value) => customerChanged(0, value)}
                onClear={() => customerChanged(0)}
            />
        </Form.Item>

        {customers.slice(1).map((user, idx) => {
            const trueIndex = idx + 1;
            return <div key={trueIndex} style={{ display: 'flex' }}>
                <CustomerSelector
                    includeFrozen={includeFrozenUsers}
                    value={user}
                    onSelected={selected => customerChanged(trueIndex, selected)}
                    onClear={() => customerChanged(trueIndex)}
                    style={{ flexGrow: 1 }}
                />
                <Button danger
                    style={{ marginLeft: '10px', alignSelf: 'center' }}
                    icon={<DeleteFilled />}
                    onClick={() => {
                        customers.splice(trueIndex, 1);
                        setCustomers(cur => [...cur]);
                        notify();
                    }} />
            </div>
        })}

        {allowMultiple && <Button
            onClick={() => customerChanged(customers.length, undefined)}
            disabled={customers.length === 0 || !customers[0] || !isValid()}>
            Add Another Account
        </Button>}
    </Space>;
}

const OwnershipSelector = (props: OwnershipSelectorProps) => {
    const { supportSilentAccounts, ...rest } = props;

    if (supportSilentAccounts) {
        return <SilentAccountSelector {...rest} />;
    }

    return <ConsignmentAccountSelector {...rest} />;
}

export default OwnershipSelector;